/**
 * LinkToItemPanel
 *
 * Reusable panel for creating a methodology_connection from a clarity artifact
 * (backplan node, outcome driver, open question) to a prioritization item.
 *
 * Wraps createConnection RPC. Deduplicates silently (the RPC handles it).
 */

import { useState, useMemo, useEffect } from 'react';
import { Link2, Search, Check, Loader2, X } from 'lucide-react';
import { useItemsStore } from '../../stores/prioritization/itemsStore';
import {
  createConnection,
  type MethodologySourceType,
  type MethodologyConnectionType,
} from '../../lib/methodologyConnectionsService';
import { getCurrentWorkspaceId } from '../../lib/supabaseSync';

const CONNECTION_TYPE_OPTIONS: { value: MethodologyConnectionType; label: string; hint: string }[] = [
  { value: 'informs', label: 'Informs', hint: 'This artifact provides context that shapes the item' },
  { value: 'motivates', label: 'Motivates', hint: 'This artifact is the reason the item exists' },
  { value: 'addresses', label: 'Addresses', hint: 'This artifact describes a problem the item solves' },
  { value: 'answers', label: 'Answers', hint: 'This artifact is a question the item resolves' },
  { value: 'derives_from', label: 'Derives from', hint: 'The item emerged directly from this artifact' },
];

interface LinkToItemPanelProps {
  sourceEntityType: MethodologySourceType;
  sourceEntityId: string;
  onLinked?: (itemId: string) => void;
  onClose?: () => void;
}

export function LinkToItemPanel({
  sourceEntityType,
  sourceEntityId,
  onLinked,
  onClose,
}: LinkToItemPanelProps) {
  const { items, fetchFromSupabase } = useItemsStore();

  // Ensure items are loaded — backplan context may not have fetched them yet.
  useEffect(() => {
    if (items.length === 0) {
      void fetchFromSupabase();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [query, setQuery] = useState('');
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [connectionType, setConnectionType] = useState<MethodologyConnectionType>('informs');
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const filtered = useMemo(() => {
    if (!query.trim()) return [];
    const q = query.toLowerCase();
    return items
      .filter((i) => i.title?.toLowerCase().includes(q) || i.id.includes(q))
      .slice(0, 8);
  }, [query, items]);

  const selectedItem = items.find((i) => i.id === selectedItemId);

  async function handleLink() {
    if (!selectedItemId) return;
    const workspaceId = getCurrentWorkspaceId();
    if (!workspaceId) {
      setError('No workspace context');
      return;
    }
    setIsSaving(true);
    setError(null);

    const { error: linkError } = await createConnection({
      workspaceId,
      sourceEntityType,
      sourceEntityId,
      targetEntityType: 'item',
      targetEntityId: selectedItemId,
      connectionType,
    });

    setIsSaving(false);
    if (linkError && linkError !== 'duplicate') {
      setError(linkError);
      return;
    }

    setSaved(true);
    onLinked?.(selectedItemId);
    setTimeout(() => {
      setSaved(false);
      setSelectedItemId(null);
      setQuery('');
    }, 1500);
  }

  return (
    <div className="border border-[var(--slate)] dark:border-[var(--slate)] bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20 p-4">
      {/* Panel header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Link2 className="h-3.5 w-3.5 text-[var(--text-ter)]" />
          <span className="text-xs font-semibold text-[var(--text-sec)]">
            Link to a prioritization item
          </span>
        </div>
        {onClose && (
          <button onClick={onClose} className="p-0.5 rounded hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--slate)]/40 text-[var(--text-sec)]">
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {/* Item search */}
      {!selectedItemId && (
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--text-ter)]" />
          <input
            autoFocus
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by item title…"
            className="w-full pl-8 pr-3 py-2 text-xs border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-400"
          />
          {filtered.length > 0 && (
            <div className="mt-1 border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] divide-y divide-[var(--rule)] dark:divide-[var(--rule)] shadow-sm max-h-48 overflow-y-auto">
              {filtered.map((item) => (
                <button
                  key={item.id}
                  onClick={() => { setSelectedItemId(item.id); setQuery(''); }}
                  className="w-full text-left px-3 py-2 text-xs text-[var(--text-pri)] dark:text-[var(--text-pri)] hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--slate)]/20 transition-colors"
                >
                  {item.title}
                  <span className="text-[var(--text-ter)] ml-2 font-mono text-[10px]">{item.id.slice(0, 8)}</span>
                </button>
              ))}
            </div>
          )}
          {query.trim() && filtered.length === 0 && (
            <p className="mt-1 text-xs text-[var(--text-sec)] px-1">No items match that query.</p>
          )}
        </div>
      )}

      {/* Selected item + connection type */}
      {selectedItemId && selectedItem && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 px-3 py-2 bg-[var(--bg-raised)] dark:bg-[var(--surface)] border border-[var(--slate)] dark:border-[var(--slate)]">
            <span className="flex-1 text-xs text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate font-medium">
              {selectedItem.title}
            </span>
            <button
              onClick={() => setSelectedItemId(null)}
              className="text-[var(--text-ter)] hover:text-[var(--text-ter)] flex-shrink-0"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          <div>
            <p className="text-[10px] font-semibold text-[var(--text-sec)] uppercase tracking-wide mb-1.5">
              Relationship type
            </p>
            <div className="space-y-1">
              {CONNECTION_TYPE_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className={`flex items-start gap-2.5 px-2.5 py-2  cursor-pointer transition-colors ${
                    connectionType === opt.value
                      ? 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40'
                      : 'hover:bg-[var(--bg-raised)]/70 dark:hover:bg-[var(--bg-raised)]/5'
                  }`}
                >
                  <input
                    type="radio"
                    name="connType"
                    value={opt.value}
                    checked={connectionType === opt.value}
                    onChange={() => setConnectionType(opt.value)}
                    className="mt-0.5 accent-indigo-600"
                  />
                  <div>
                    <span className="text-xs font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                      {opt.label}
                    </span>
                    <p className="text-[10px] text-[var(--text-ter)] dark:text-[var(--text-ter)] leading-snug">
                      {opt.hint}
                    </p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {error && (
            <p className="text-xs text-[var(--coral)] dark:text-[var(--coral)]">{error}</p>
          )}

          <button
            onClick={handleLink}
            disabled={isSaving || saved}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-[var(--slate)] hover:bg-[var(--slate)] disabled:opacity-60 text-white text-xs font-medium transition-colors"
          >
            {isSaving ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : saved ? (
              <Check className="h-3.5 w-3.5" />
            ) : (
              <Link2 className="h-3.5 w-3.5" />
            )}
            {saved ? 'Linked' : 'Create link'}
          </button>
        </div>
      )}
    </div>
  );
}
